/**
 * Minimal ambient types for the Web Speech API.
 *
 * TypeScript's DOM library still does not ship `SpeechRecognition`, and the
 * unprefixed constructor only reached Chrome recently while Safari has shipped
 * `webkitSpeechRecognition` since 14.1. Declaring just the surface actually
 * used keeps the app honest about how little of this API can be relied on.
 */

export interface SpeechRecognitionAlternative {
  readonly transcript: string
  readonly confidence: number
}

export interface SpeechRecognitionResult {
  readonly isFinal: boolean
  readonly length: number
  item(index: number): SpeechRecognitionAlternative
  [index: number]: SpeechRecognitionAlternative
}

export interface SpeechRecognitionResultList {
  readonly length: number
  item(index: number): SpeechRecognitionResult
  [index: number]: SpeechRecognitionResult
}

export interface SpeechRecognitionEventLike extends Event {
  /**
   * Index of the first result that is new in this event. Chrome's result list
   * grows for the life of the recogniser, so iterating from zero re-emits
   * everything already handled.
   */
  readonly resultIndex: number
  readonly results: SpeechRecognitionResultList
}

export type SpeechRecognitionErrorCode =
  | 'no-speech'
  | 'aborted'
  | 'audio-capture'
  | 'network'
  | 'not-allowed'
  | 'service-not-allowed'
  | 'bad-grammar'
  | 'language-not-supported'

export interface SpeechRecognitionErrorEventLike extends Event {
  readonly error: SpeechRecognitionErrorCode
  readonly message: string
}

export interface SpeechRecognitionLike extends EventTarget {
  lang: string
  continuous: boolean
  interimResults: boolean
  maxAlternatives: number
  start(): void
  stop(): void
  abort(): void
  onresult: ((event: SpeechRecognitionEventLike) => void) | null
  onerror: ((event: SpeechRecognitionErrorEventLike) => void) | null
  onend: ((event: Event) => void) | null
  onstart: ((event: Event) => void) | null
  onspeechstart: ((event: Event) => void) | null
  onspeechend: ((event: Event) => void) | null
}

type SpeechRecognitionConstructor = new () => SpeechRecognitionLike

interface SpeechWindow {
  SpeechRecognition?: SpeechRecognitionConstructor
  webkitSpeechRecognition?: SpeechRecognitionConstructor
  speechSynthesis?: SpeechSynthesis
  SpeechSynthesisUtterance?: typeof SpeechSynthesisUtterance
}

export function getSpeechRecognitionConstructor(): SpeechRecognitionConstructor | null {
  if (typeof window === 'undefined') return null
  const scoped = window as unknown as SpeechWindow
  return scoped.SpeechRecognition ?? scoped.webkitSpeechRecognition ?? null
}

export function getSpeechSynthesis(): SpeechSynthesis | null {
  if (typeof window === 'undefined') return null
  const scoped = window as unknown as SpeechWindow
  return scoped.speechSynthesis ?? null
}

export function getUtteranceConstructor(): typeof SpeechSynthesisUtterance | null {
  if (typeof window === 'undefined') return null
  const scoped = window as unknown as SpeechWindow
  return scoped.SpeechSynthesisUtterance ?? null
}

/**
 * iOS puts the audio session into record mode when the microphone opens, which
 * makes the synthesiser duck the input. Treating those platforms as half
 * duplex is not a limitation to hide, it is one to design around: the
 * "talk to interrupt" hint is suppressed where interrupting will not work.
 */
export function isHalfDuplexPlatform(): boolean {
  if (typeof navigator === 'undefined') return false
  const ua = navigator.userAgent
  const isIOS = /iPad|iPhone|iPod/.test(ua)
  // iPadOS reports as a Mac, distinguishable only by touch support.
  const isIPadOS = /Macintosh/.test(ua) && typeof document !== 'undefined' && navigator.maxTouchPoints > 1
  return isIOS || isIPadOS
}

export function isAndroid(): boolean {
  return typeof navigator !== 'undefined' && /Android/.test(navigator.userAgent)
}

/**
 * `getVoices()` returns an empty list on the first call in every browser. The
 * `voiceschanged` event fixes it in Chrome and Firefox, never fires on Safari
 * 14 to 15, and the whole thing can simply not resolve. So: listen, poll, and
 * give up after a bounded wait rather than leaving the app wedged waiting for
 * a voice that is not coming.
 */
export function loadVoices(timeoutMs = 3000): Promise<SpeechSynthesisVoice[]> {
  const synthesis = getSpeechSynthesis()
  if (!synthesis) return Promise.resolve([])

  const immediate = synthesis.getVoices()
  if (immediate.length > 0) return Promise.resolve(immediate)

  return new Promise((resolve) => {
    let settled = false
    const finish = (voices: SpeechSynthesisVoice[]) => {
      if (settled) return
      settled = true
      clearInterval(poll)
      clearTimeout(deadline)
      synthesis.removeEventListener?.('voiceschanged', onChange)
      resolve(voices)
    }

    const onChange = () => finish(synthesis.getVoices())
    synthesis.addEventListener?.('voiceschanged', onChange)

    const poll = setInterval(() => {
      const voices = synthesis.getVoices()
      if (voices.length > 0) finish(voices)
    }, 120)

    const deadline = setTimeout(() => finish(synthesis.getVoices()), timeoutMs)
  })
}

/**
 * Picks the best voice for a set of BCP-47 tags, most specific first.
 * Device-local voices win over network ones: they start faster, they work
 * offline, and they do not get cut off partway through the way Chrome's
 * remote voices do.
 */
export function pickVoice(
  voices: readonly SpeechSynthesisVoice[],
  tags: readonly string[],
): SpeechSynthesisVoice | null {
  for (const tag of tags) {
    const wanted = tag.toLowerCase()
    const matches = voices.filter((voice) => {
      const lang = voice.lang.toLowerCase().replace('_', '-')
      return lang === wanted || lang.startsWith(wanted + '-')
    })
    if (matches.length === 0) continue
    return matches.find((voice) => voice.localService) ?? (matches[0] as SpeechSynthesisVoice)
  }
  return null
}

/**
 * Splits text into chunks a synthesiser will actually finish. Chrome's remote
 * voices stop around 14 to 15 seconds with no `end` and no `error`, so long
 * lines have to be broken up. Splits on sentence boundaries where it can, and
 * on word boundaries where it cannot.
 */
export function chunkForSpeech(text: string, maxLength = 180): string[] {
  const trimmed = text.trim()
  if (trimmed.length <= maxLength) return trimmed.length > 0 ? [trimmed] : []

  const sentences = trimmed.split(/(?<=[.!?])\s+/)
  const chunks: string[] = []
  let current = ''

  const flush = () => {
    if (current.trim().length > 0) chunks.push(current.trim())
    current = ''
  }

  for (const sentence of sentences) {
    if (sentence.length > maxLength) {
      flush()
      let remaining = sentence
      while (remaining.length > maxLength) {
        const cut = remaining.lastIndexOf(' ', maxLength)
        const at = cut > maxLength / 2 ? cut : maxLength
        chunks.push(remaining.slice(0, at).trim())
        remaining = remaining.slice(at).trim()
      }
      current = remaining
      continue
    }

    if ((current + ' ' + sentence).trim().length > maxLength) flush()
    current = current.length === 0 ? sentence : current + ' ' + sentence
  }

  flush()
  return chunks
}

/** Rough spoken duration, used to size the safety timeout on an utterance. */
export function estimateSpeechDurationMs(text: string, rate = 1): number {
  // ~14 characters per second at rate 1 is a reasonable middle for both
  // English and Hausa. Only used for timeouts, so erring long is harmless.
  return Math.max(500, (text.length / 14) * 1000) / Math.max(0.5, rate)
}
